/**
 * Trainova AI — Tenant Settings Portal
 *
 * Implements the full interactive Tenant Settings interface with 3 tabs:
 * 1. Organisation (Company profile, branding, AI context, policies, regional formats)
 * 2. Custom fields (Learner user-defined metadata schema & custom attributes)
 * 3. Notifications (Channels, timing, quiet hours, sender identity, templates, live test email)
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../stores/authStore';
import api from '../../../services/api';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { TenantLanguageSettingsTab } from '../components/TenantLanguageSettingsTab';
import '../admin-portal.css';

interface ChannelConfig {
  email: boolean;
  inApp: boolean;
  label: string;
  sub: string;
  recipientGroup: 'learners' | 'admins';
}

interface TemplateConfig {
  key: string;
  name: string;
  category: 'learners' | 'admins';
  subject: string;
  heading: string;
  description: string;
  boxTitle: string;
  boxMeta: string;
  ctaText: string;
}

interface CustomFieldDef {
  id: string;
  label: string;
  key: string;
  type: 'select' | 'text' | 'number';
  options?: string[];
  required: boolean;
  isActive: boolean;
  usedInReports: boolean;
}

interface OrgProfileData {
  companyName: string;
  displayName: string;
  slug: string;
  industry: string;
  companySize: string;
  founded: string;
  website: string;
  headquarters: string;
  operatingCities: string;
  logoUrl: string;
  brandColor: string;
  overview: string;
  products: string[];
  customerTypes: string[];
  brandVoice: 'Warm & professional' | 'Formal' | 'Casual';
  glossary: Array<{ say: string; insteadOf: string }>;
  policies: Array<{ title: string; desc: string; file?: string }>;
  timezone: string;
  dateFormat: string;
  currency: string;
  workingDays: string;
}

export interface SmtpConfig {
  enabled: boolean;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  hasPassword?: boolean;
  from: string;
  fromName: string;
  replyTo: string;
}

interface NotificationSettingsData {
  tenantId: string;
  tenantName: string;
  channels: Record<string, ChannelConfig>;
  timing: {
    reminderDays: number[];
    maxOverdueNudgeWeeks: number;
    quietHoursStart: string;
    quietHoursEnd: string;
    quietHoursTz: string;
    copyManagerOnOverdue: boolean;
  };
  sender: {
    fromName: string;
    replyToEmail: string;
    fromEmail: string;
  };
  smtp: SmtpConfig;
  templates: Record<string, TemplateConfig>;
}

const DEFAULT_ORG_PROFILE: OrgProfileData = {
  companyName: 'TrainerBay Automotive Services Pvt Ltd',
  displayName: 'TrainerBay Academy',
  slug: 'demo-trainova',
  industry: 'Automotive retail & service',
  companySize: '1,001 – 5,000 employees',
  founded: '2011',
  website: 'https://www.trainerbay.in',
  headquarters: 'Pune, Maharashtra',
  operatingCities: 'India · 42 cities',
  logoUrl: '',
  brandColor: '#0D4FBD',
  overview: 'TrainerBay runs 118 multi-brand car service centres and 36 sales showrooms across India. We service 2.4 lakh vehicles a year, from periodic maintenance to accident repair. Our promise is "no surprise bills": every extra job is explained and approved by the customer before work starts.',
  products: ['Periodic service', 'Brake & suspension', 'Accident repair', 'Extended warranty', 'New cars', 'Pre-owned cars'],
  customerTypes: ['Individual owners', 'Corporate fleets', 'Insurance walk-ins', 'First-time buyers'],
  brandVoice: 'Warm & professional',
  glossary: [
    { say: 'Service Advisor', insteadOf: 'Mechanic' },
    { say: 'Job card', insteadOf: 'Bill / ticket' },
    { say: 'Estimate, approved by you', insteadOf: 'Extra charges' },
    { say: 'Guest', insteadOf: 'Walk-in' },
  ],
  policies: [
    { title: 'Customer approval for extra work', desc: 'Any job over ₹2,000 not on original estimate needs approval before starting', file: 'SOP-14.pdf' },
    { title: 'Parts warranty', desc: '6 months or 10,000 km on replaced parts, whichever comes first' },
    { title: 'Delivery time promise', desc: 'If car is late, inform customer at least 2 hours before promised time' },
    { title: 'Refunds & cancellations', desc: 'Full refund on advance payments cancelled before work starts', file: 'Refund-policy.pdf' },
  ],
  timezone: '(UTC+05:30) India Standard Time',
  dateFormat: '23 Sep 2026',
  currency: '₹ INR',
  workingDays: 'Mon – Sat',
};

const DEFAULT_CUSTOM_FIELDS: CustomFieldDef[] = [
  { id: 'fld_designation', label: 'Designation / Role', key: 'designation', type: 'select', options: ['Service Advisor', 'Technician', 'Floor Manager', 'Diagnostic Specialist', 'L&D Coach'], required: true, isActive: true, usedInReports: true },
  { id: 'fld_team', label: 'Team / Unit', key: 'team', type: 'select', options: ['Body & Paint', 'Diagnostics', 'Express Service', 'General Maintenance', 'High-Voltage EV Unit'], required: true, isActive: true, usedInReports: true },
  { id: 'fld_region', label: 'Region / Cluster', key: 'region', type: 'select', options: ['West', 'North', 'South', 'East', 'Central Hub'], required: true, isActive: true, usedInReports: true },
  { id: 'fld_grade', label: 'Seniority Grade', key: 'grade', type: 'select', options: ['Level 1 (Apprentice)', 'Level 2 (Junior)', 'Level 3 (Senior)', 'Level 4 (Master)'], required: false, isActive: true, usedInReports: true },
  { id: 'fld_branch', label: 'Branch / Dealership Code', key: 'branchCode', type: 'text', required: false, isActive: true, usedInReports: false },
  { id: 'fld_cost_center', label: 'Cost Center ID', key: 'costCenter', type: 'text', required: false, isActive: true, usedInReports: false },
];

const DEFAULT_SETTINGS: NotificationSettingsData = {
  tenantId: 'default',
  tenantName: 'TrainerBay',
  channels: {
    simulation_assigned: {
      email: true,
      inApp: true,
      label: 'Simulation assigned',
      sub: 'When an admin assigns a simulation',
      recipientGroup: 'learners',
    },
    due_date_reminder: {
      email: true,
      inApp: true,
      label: 'Due-date reminder',
      sub: '3 days and 1 day before the due date',
      recipientGroup: 'learners',
    },
    overdue: {
      email: true,
      inApp: true,
      label: 'Overdue',
      sub: 'Day after the due date, then weekly',
      recipientGroup: 'learners',
    },
    results_ready: {
      email: true,
      inApp: true,
      label: 'Results ready',
      sub: 'When scoring finishes, usually under 2 minutes',
      recipientGroup: 'learners',
    },
    session_flagged: {
      email: true,
      inApp: true,
      label: 'Session flagged',
      sub: 'Camera off, second person in frame, tab switches',
      recipientGroup: 'admins',
    },
    account_locked: {
      email: true,
      inApp: false,
      label: 'Learner account locked',
      sub: 'After 5 failed sign-ins',
      recipientGroup: 'admins',
    },
    weekly_summary: {
      email: true,
      inApp: false,
      label: 'Weekly summary',
      sub: 'Mondays at 9:00 AM',
      recipientGroup: 'admins',
    },
    plan_limit_80: {
      email: true,
      inApp: true,
      label: 'Plan limit at 80%',
      sub: 'Seats or simulation minutes',
      recipientGroup: 'admins',
    },
  },
  timing: {
    reminderDays: [3, 1],
    maxOverdueNudgeWeeks: 4,
    quietHoursStart: '9:00 PM',
    quietHoursEnd: '8:00 AM',
    quietHoursTz: 'India Standard Time',
    copyManagerOnOverdue: true,
  },
  sender: {
    fromName: 'TrainerBay Academy',
    replyToEmail: 'academy@trainerbay.in',
    fromEmail: 'no-reply@mail.trainova.ai',
  },
  smtp: {
    enabled: false,
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    hasPassword: false,
    from: '',
    fromName: 'TrainerBay Academy',
    replyTo: 'academy@trainerbay.in',
  },
  templates: {
    due_date_reminder: {
      key: 'due_date_reminder',
      name: 'Due-date reminder',
      category: 'learners',
      subject: 'Due in 3 days: Workshop handover: unexpected brake repair',
      heading: 'Hi {{first_name}}, your simulation is due soon',
      description: 'Take 10 minutes to practise before the due date. You can attempt it up to 3 times.',
      boxTitle: 'Workshop handover: unexpected brake repair',
      boxMeta: 'Due on {{due_date}} · about 10 min · 1 of 3 attempts used',
      ctaText: 'Open simulation',
    },
    simulation_assigned: {
      key: 'simulation_assigned',
      name: 'Simulation assigned',
      category: 'learners',
      subject: 'New simulation assigned: Workshop handover: unexpected brake repair',
      heading: 'Hi {{first_name}}, you have a new simulation to complete',
      description: 'Your training admin has assigned you a scenario. Please complete it before the due date.',
      boxTitle: 'Workshop handover: unexpected brake repair',
      boxMeta: 'Due on {{due_date}} · about 10 min · 3 attempts allowed',
      ctaText: 'Start simulation',
    },
    overdue: {
      key: 'overdue',
      name: 'Overdue',
      category: 'learners',
      subject: 'Action required: Workshop handover: unexpected brake repair is overdue',
      heading: 'Hi {{first_name}}, your simulation is overdue',
      description: 'Your assigned simulation was due recently. Please complete it as soon as possible.',
      boxTitle: 'Workshop handover: unexpected brake repair',
      boxMeta: 'Overdue since {{due_date}} · 1 of 3 attempts used',
      ctaText: 'Complete simulation',
    },
    results_ready: {
      key: 'results_ready',
      name: 'Results ready',
      category: 'learners',
      subject: 'Results ready: Workshop handover: unexpected brake repair (Score: 84%)',
      heading: 'Hi {{first_name}}, your simulation results are ready',
      description: 'Your performance has been evaluated by Trainova AI. Review your scores and feedback breakdown.',
      boxTitle: 'Workshop handover: unexpected brake repair',
      boxMeta: 'Completed on {{due_date}} · Score: 84%',
      ctaText: 'View full results',
    },
    session_flagged: {
      key: 'session_flagged',
      name: 'Session flagged',
      category: 'admins',
      subject: 'Alert: Session flagged for review - {{learner_name}}',
      heading: 'Admin Notice: Simulation session flagged',
      description: 'Proctoring triggers detected during learner session: camera turned off or tab switched.',
      boxTitle: 'Simulation: Workshop handover: unexpected brake repair',
      boxMeta: 'Learner: {{learner_name}} · Flagged recently',
      ctaText: 'Inspect audit playback',
    },
    account_locked: {
      key: 'account_locked',
      name: 'Learner account locked',
      category: 'admins',
      subject: 'Security Alert: Account locked after 5 failed attempts - {{learner_name}}',
      heading: 'Admin Notice: Learner account temporarily locked',
      description: 'A learner account exceeded maximum failed authentication attempts.',
      boxTitle: 'Learner: {{learner_name}} (learner@trainerbay.in)',
      boxMeta: 'Locked recently · IP: 192.168.1.45',
      ctaText: 'Manage user access',
    },
    weekly_summary: {
      key: 'weekly_summary',
      name: 'Weekly summary',
      category: 'admins',
      subject: 'Trainova Weekly Summary: 84% completion rate this week',
      heading: 'Weekly Organization Simulation Digest',
      description: 'Here is your weekly digest of completed simulations, top skills, and overdue learners.',
      boxTitle: 'Active Learners: 48 · Total Attempts: 142',
      boxMeta: 'Top Competency: Active Listening (88%) · Attention Needed: Objections',
      ctaText: 'Open reports dashboard',
    },
    plan_limit_80: {
      key: 'plan_limit_80',
      name: 'Plan limit at 80%',
      category: 'admins',
      subject: 'Plan Alert: Organization reached 80% of simulation minutes',
      heading: 'Usage Threshold Warning (80%)',
      description: 'Your organization has utilized 80% of your allocated monthly simulation minutes.',
      boxTitle: 'Used: 8,000 / 10,000 minutes (80%)',
      boxMeta: 'Billing cycle resets in 8 days',
      ctaText: 'Upgrade or manage plan',
    },
  },
};

export const TenantNotificationSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active Tab: 'org' | 'custom_fields' | 'notifications' | 'languages'
  const initialTab = searchParams.get('tab') === 'org'
    ? 'org'
    : searchParams.get('tab') === 'custom_fields'
      ? 'custom_fields'
      : searchParams.get('tab') === 'languages'
        ? 'languages'
        : 'notifications';

  const [activeTab, setActiveTab] = useState<'org' | 'custom_fields' | 'notifications' | 'languages'>(initialTab);

  // Data States
  const [settings, setSettings] = useState<NotificationSettingsData>(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState<NotificationSettingsData>(DEFAULT_SETTINGS);
  const [orgProfile, setOrgProfile] = useState<OrgProfileData>(DEFAULT_ORG_PROFILE);
  const [initialOrgProfile, setInitialOrgProfile] = useState<OrgProfileData>(DEFAULT_ORG_PROFILE);
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>(DEFAULT_CUSTOM_FIELDS);

  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('due_date_reminder');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [notificationBanner, setNotificationBanner] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Modals
  const [showTestModal, setShowTestModal] = useState<boolean>(false);
  const [testEmail, setTestEmail] = useState<string>(user?.email || 'admin@trainerbay.in');
  const [isSendingTest, setIsSendingTest] = useState<boolean>(false);

  const [showEditTemplateModal, setShowEditTemplateModal] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateConfig | null>(null);

  const [showAddFieldModal, setShowAddFieldModal] = useState<boolean>(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<'select' | 'text' | 'number'>('select');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);

  // SMTP Configuration States
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showSmtpTestModal, setShowSmtpTestModal] = useState<boolean>(false);
  const [smtpTestRecipient, setSmtpTestRecipient] = useState<string>(user?.email || 'admin@trainerbay.in');
  const [isSendingSmtpTest, setIsSendingSmtpTest] = useState<boolean>(false);

  // Sync tab with URL search parameter
  const handleTabChange = (tab: 'org' | 'custom_fields' | 'notifications' | 'languages') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Load settings on mount
  useEffect(() => {
    async function fetchAllSettings() {
      try {
        setIsLoading(true);
        const [notifRes, settingsRes] = await Promise.allSettled([
          api.get<{ success: boolean; data: any }>('/api/v1/admin/tenant/settings/notifications'),
          api.get<{ success: boolean; data: any }>('/api/v1/admin/tenant/settings'),
        ]);

        if (notifRes.status === 'fulfilled' && notifRes.value.data?.data) {
          const data = notifRes.value.data.data;
          const merged: NotificationSettingsData = {
            tenantId: data.tenantId || DEFAULT_SETTINGS.tenantId,
            tenantName: data.tenantName || DEFAULT_SETTINGS.tenantName,
            channels: { ...DEFAULT_SETTINGS.channels, ...(data.channels || {}) },
            timing: { ...DEFAULT_SETTINGS.timing, ...(data.timing || {}) },
            sender: { ...DEFAULT_SETTINGS.sender, ...(data.sender || {}) },
            smtp: { ...DEFAULT_SETTINGS.smtp, ...(data.smtp || {}) },
            templates: { ...DEFAULT_SETTINGS.templates, ...(data.templates || {}) },
          };
          setSettings(merged);
          setInitialSettings(merged);
        }

        if (settingsRes.status === 'fulfilled' && settingsRes.value.data?.data) {
          const s = settingsRes.value.data.data;
          setOrgProfile((prev) => ({
            ...prev,
            companyName: s.tenantName || s.name || prev.companyName,
            displayName: s.displayName || s.tenantName || prev.displayName,
            slug: s.tenantSlug || s.slug || user?.tenantSlug || prev.slug,
            brandColor: s.primaryColor || prev.brandColor,
            logoUrl: s.logoUrl || prev.logoUrl,
          }));
          setInitialOrgProfile((prev) => ({
            ...prev,
            companyName: s.tenantName || s.name || prev.companyName,
            displayName: s.displayName || s.tenantName || prev.displayName,
            slug: s.tenantSlug || s.slug || user?.tenantSlug || prev.slug,
          }));
        } else if (user?.tenantSlug) {
          setOrgProfile((prev) => ({ ...prev, slug: user.tenantSlug || prev.slug }));
        }
      } catch (err) {
        console.warn('Failed to load settings, using defaults.', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllSettings();
  }, [user?.userId, user?.tenantId]);

// Notifications handlers
const handleToggleChannel = (key: string, channel: 'email' | 'inApp') => {
  setSettings((prev) => {
    const current = prev.channels[key] || { email: true, inApp: true, label: key, sub: '', recipientGroup: 'learners' };
    const updated = {
      ...prev,
      channels: {
        ...prev.channels,
        [key]: {
          ...current,
          [channel]: !current[channel],
        },
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleToggleManagerCopy = () => {
  setSettings((prev) => {
    const updated = {
      ...prev,
      timing: {
        ...prev.timing,
        copyManagerOnOverdue: !prev.timing.copyManagerOnOverdue,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleNudgeWeeksChange = (delta: number) => {
  setSettings((prev) => {
    const newWeeks = Math.max(1, Math.min(12, prev.timing.maxOverdueNudgeWeeks + delta));
    const updated = {
      ...prev,
      timing: {
        ...prev.timing,
        maxOverdueNudgeWeeks: newWeeks,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleAddReminderDay = () => {
  const promptVal = window.prompt('Enter days before due date for reminder (e.g. 5 or 7):', '5');
  if (!promptVal) return;
  const days = parseInt(promptVal.trim(), 10);
  if (isNaN(days) || days <= 0 || days > 30) {
    alert('Please enter a valid number of days between 1 and 30.');
    return;
  }
  if (settings.timing.reminderDays.includes(days)) {
    alert('This reminder day is already configured.');
    return;
  }

  setSettings((prev) => {
    const updatedDays = [...prev.timing.reminderDays, days].sort((a, b) => b - a);
    const updated = {
      ...prev,
      timing: {
        ...prev.timing,
        reminderDays: updatedDays,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleRemoveReminderDay = (day: number) => {
  setSettings((prev) => {
    const updatedDays = prev.timing.reminderDays.filter((d) => d !== day);
    const updated = {
      ...prev,
      timing: {
        ...prev.timing,
        reminderDays: updatedDays,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleSenderChange = (field: 'fromName' | 'replyToEmail', value: string) => {
  setSettings((prev) => {
    const updated = {
      ...prev,
      sender: {
        ...prev.sender,
        [field]: value,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

const handleSmtpChange = (field: keyof SmtpConfig, value: any) => {
  setSettings((prev) => {
    const currentSmtp = prev.smtp || DEFAULT_SETTINGS.smtp;
    const updatedSmtp: SmtpConfig = {
      ...currentSmtp,
      [field]: value,
    };
    const updated: NotificationSettingsData = {
      ...prev,
      smtp: updatedSmtp,
    };
    setIsDirty(true);
    return updated;
  });
};

const handleTestSmtpConnection = async () => {
  const smtp: SmtpConfig = settings.smtp || DEFAULT_SETTINGS.smtp;
  if (!smtp.host) {
    setConnectionStatus({ type: 'error', message: 'Please enter an SMTP Host before testing connection.' });
    return;
  }
  try {
    setIsTestingConnection(true);
    setConnectionStatus(null);
    const res: any = await api.post('/api/v1/admin/tenant/settings/smtp/test-connection', {
      host: smtp.host,
      port: smtp.port,
      secure: smtp.secure,
      user: smtp.user,
      pass: smtp.pass === '••••••••' ? undefined : smtp.pass,
    });
    setConnectionStatus({
      type: 'success',
      message: res?.data?.message || res?.message || 'SMTP connection verified successfully! Socket handshake succeeded.',
    });
  } catch (err: any) {
    setConnectionStatus({
      type: 'error',
      message: err?.response?.data?.error || err.message || 'SMTP connection failed. Verify host, port, and firewall.',
    });
  } finally {
    setIsTestingConnection(false);
  }
};

const handleSendSmtpTestEmail = async () => {
  const smtp: SmtpConfig = settings.smtp || DEFAULT_SETTINGS.smtp;
  const recipient = smtpTestRecipient || testEmail || user?.email;
  if (!recipient) {
    alert('Please enter a recipient email address.');
    return;
  }
  try {
    setIsSendingSmtpTest(true);
    const res: any = await api.post('/api/v1/admin/tenant/settings/smtp/test-email', {
      host: smtp.host,
      port: smtp.port,
      secure: smtp.secure,
      user: smtp.user,
      pass: smtp.pass === '••••••••' ? undefined : smtp.pass,
      from: smtp.from || settings.sender.fromEmail,
      fromName: smtp.fromName || settings.sender.fromName,
      replyTo: smtp.replyTo || settings.sender.replyToEmail,
      to: recipient,
    });
    setShowSmtpTestModal(false);
    setNotificationBanner({
      type: 'success',
      message: res?.data?.message || res?.message || `Test email dispatched to ${recipient}!`,
    });
    setTimeout(() => setNotificationBanner(null), 5000);
  } catch (err: any) {
    alert(err?.response?.data?.error || err.message || 'Failed to dispatch test email.');
  } finally {
    setIsSendingSmtpTest(false);
  }
};

const handleQuietHoursChange = (field: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
  setSettings((prev) => {
    const updated = {
      ...prev,
      timing: {
        ...prev.timing,
        [field]: value,
      },
    };
    setIsDirty(true);
    return updated;
  });
};

// Org Profile Handlers
const handleOrgFieldChange = (field: keyof OrgProfileData, value: any) => {
  setOrgProfile((prev) => ({
    ...prev,
    [field]: value,
  }));
  setIsDirty(true);
};

// Save All Settings
const handleSaveChanges = async () => {
  try {
    setIsSaving(true);
    if (activeTab === 'notifications') {
      await api.patch('/api/v1/admin/tenant/settings/notifications', settings);
      setInitialSettings(settings);
    } else if (activeTab === 'org') {
      await api.patch('/api/v1/admin/tenant/settings', {
        name: orgProfile.companyName,
        displayName: orgProfile.displayName,
        slug: orgProfile.slug,
        primaryColor: orgProfile.brandColor,
        logoUrl: orgProfile.logoUrl,
      });
      setInitialOrgProfile(orgProfile);
    }
    setIsDirty(false);
    setNotificationBanner({
      type: 'success',
      message: 'Settings updated and saved successfully.',
    });
    setTimeout(() => setNotificationBanner(null), 4000);
  } catch (err: any) {
    setNotificationBanner({
      type: 'error',
      message: err.message || 'Failed to save settings. Please check your connection.',
    });
  } finally {
    setIsSaving(false);
  }
};

const handleDiscard = () => {
  setSettings(initialSettings);
  setOrgProfile(initialOrgProfile);
  setIsDirty(false);
};

const handleSendTestEmail = async () => {
  try {
    setIsSendingTest(true);
    const res: any = await api.post('/api/v1/admin/tenant/settings/notifications/test-email', {
      templateKey: selectedTemplateKey,
      recipientEmail: testEmail,
    });

    setShowTestModal(false);
    setNotificationBanner({
      type: 'success',
      message: res?.message || `Test email dispatched to ${testEmail}! Check your inbox.`,
    });
    setTimeout(() => setNotificationBanner(null), 4000);
  } catch (err) {
    alert('Failed to send test email.');
  } finally {
    setIsSendingTest(false);
  }
};

const handleOpenEditTemplate = (key: string) => {
  setSelectedTemplateKey(key);
  const tpl = settings.templates[key] || DEFAULT_SETTINGS.templates[key];
  setEditingTemplate({ ...tpl });
  setShowEditTemplateModal(true);
};

const handleSaveTemplateModal = () => {
  if (!editingTemplate) return;
  setSettings((prev) => {
    const updated = {
      ...prev,
      templates: {
        ...prev.templates,
        [editingTemplate.key]: editingTemplate,
      },
    };
    setIsDirty(true);
    return updated;
  });
  setShowEditTemplateModal(false);
  setNotificationBanner({
    type: 'success',
    message: `Template '${editingTemplate.name}' updated. Click 'Save changes' to persist.`,
  });
  setTimeout(() => setNotificationBanner(null), 4000);
};

const handleResetTemplateToDefault = (key: string) => {
  const def = DEFAULT_SETTINGS.templates[key];
  if (!def) return;
  if (window.confirm(`Reset template '${def.name}' to default?`)) {
    setSettings((prev) => {
      const updated = {
        ...prev,
        templates: {
          ...prev.templates,
          [key]: { ...def },
        },
      };
      setIsDirty(true);
      return updated;
    });
    setNotificationBanner({
      type: 'success',
      message: `Template '${def.name}' has been restored to default.`,
    });
    setTimeout(() => setNotificationBanner(null), 3000);
  }
};

// Custom Fields Handlers
const handleAddCustomField = () => {
  if (!newFieldLabel.trim()) return;
  const key = newFieldLabel.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 20);
  const opts = newFieldType === 'select'
    ? newFieldOptions.split(',').map((o) => o.trim()).filter(Boolean)
    : undefined;

  const newField: CustomFieldDef = {
    id: `fld_${Date.now()}`,
    label: newFieldLabel.trim(),
    key,
    type: newFieldType,
    options: opts,
    required: newFieldRequired,
    isActive: true,
    usedInReports: true,
  };

  setCustomFields((prev) => [...prev, newField]);
  setShowAddFieldModal(false);
  setNewFieldLabel('');
  setNewFieldOptions('');
  setNewFieldRequired(false);
  setIsDirty(true);
  setNotificationBanner({
    type: 'success',
    message: `Custom field '${newField.label}' added successfully.`,
  });
  setTimeout(() => setNotificationBanner(null), 3000);
};

const handleToggleFieldRequired = (id: string) => {
  setCustomFields((prev) =>
    prev.map((f) => (f.id === id ? { ...f, required: !f.required } : f))
  );
  setIsDirty(true);
};

const handleToggleFieldActive = (id: string) => {
  setCustomFields((prev) =>
    prev.map((f) => (f.id === id ? { ...f, isActive: !f.isActive } : f))
  );
  setIsDirty(true);
};

const activeTemplate = settings.templates[selectedTemplateKey] || DEFAULT_SETTINGS.templates['due_date_reminder'];

return (
  <div className="ad-reports-container">
    {/* Standardized Admin Header & Banner */}
    <AdminPageHeader
      breadcrumbs={[
        { label: 'Admin', to: '/admin' },
        { label: 'Settings', to: '/admin/settings' },
        {
          label:
            activeTab === 'org'
              ? 'Organisation Profile'
              : activeTab === 'custom_fields'
                ? 'Custom Fields & Schema'
                : activeTab === 'languages'
                  ? 'Languages & Regional Locales'
                  : 'Notifications & Alerts',
        },
      ]}
      icon="⚙️"
      title="Settings & Configuration"
      badge="Governance & Customization"
      description={`Manage organisation profile, learner custom attribute schema, automated notification templates, and multilingual settings for ${orgProfile.displayName || settings.tenantName || 'Organization'}.`}
    />

    {isLoading ? (
      <div style={{ background: '#FFFFFF', border: '1px solid var(--line, #E2E8F0)', borderRadius: '14px', padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
        <div className="dashboard-spinner" style={{ margin: '0 auto 16px' }} />
        <p style={{ color: 'var(--muted, #64748B)', fontSize: '14px', fontWeight: 500, margin: 0 }}>
          Loading organization preferences & notification templates…
        </p>
      </div>
    ) : (
      <>
        {/* Modern High-Polish Sub-tabs */}
        <div className="ad-tabsS" style={{ marginBottom: '20px' }}>
          <span
            className={activeTab === 'org' ? 'ad-on' : ''}
            onClick={() => handleTabChange('org')}
          >
            🏢 Organisation Profile
          </span>
          <span
            className={activeTab === 'custom_fields' ? 'ad-on' : ''}
            onClick={() => handleTabChange('custom_fields')}
          >
            🏷️ Custom Fields & Schema
          </span>
          <span
            className={activeTab === 'notifications' ? 'ad-on' : ''}
            onClick={() => handleTabChange('notifications')}
          >
            🔔 Notifications & Templates
          </span>
          <span
            className={activeTab === 'languages' ? 'ad-on' : ''}
            onClick={() => handleTabChange('languages')}
          >
            🌐 Languages & Locales
          </span>
        </div>

        {/* Notification Banner */}
        {notificationBanner && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px',
              background: notificationBanner.type === 'success' ? 'var(--ok-soft)' : 'var(--bad-soft)',
              color: notificationBanner.type === 'success' ? 'var(--ok)' : 'var(--bad)',
              border: `1px solid ${notificationBanner.type === 'success' ? '#A7F3D0' : '#FECACA'}`,
            }}
          >
            <span>{notificationBanner.type === 'success' ? '✓' : '⚠️'}</span>
            <span>{notificationBanner.message}</span>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 1: ORGANISATION PROFILE & BRANDING                                    */}
        {/* ========================================================================= */}
        {activeTab === 'org' && (
          <div>
            <div className="ad-sgrid">
              {/* Left Column */}
              <div className="ad-scol">
                {/* Card 1: Company Profile */}
                <div className="ad-sc">
                  <div className="ad-sch" style={{ justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span className="ad-si">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="6" rx="1.5" />
                          <rect x="3" y="14" width="8" height="6" rx="1.5" />
                          <rect x="13" y="14" width="8" height="6" rx="1.5" />
                          <path d="M12 10v4M7 14v-2h10v2" />
                        </svg>
                      </span>
                      <div>
                        <h3>Company profile</h3>
                        <p>Basic organization identity and branding</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ad-btn"
                      style={{ fontSize: '12.5px', padding: '5px 10px' }}
                      onClick={() => {
                        const slug = orgProfile.slug || user?.tenantSlug || 'demo-trainova';
                        const loginUrl = `${window.location.origin}/login/${slug}`;
                        navigator.clipboard.writeText(loginUrl);
                        alert(`Copied direct login URL: ${loginUrl}`);
                      }}
                      title="Copy direct learner login link for this organization"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy Login Link
                    </button>
                  </div>

                  <div className="ad-scb">
                    <div className="ad-g2">
                      <div className="ad-fld">
                        <label>Registered company name</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.companyName}
                          onChange={(e) => handleOrgFieldChange('companyName', e.target.value)}
                        />
                      </div>
                      <div className="ad-fld">
                        <label>Display name for learners</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.displayName}
                          onChange={(e) => handleOrgFieldChange('displayName', e.target.value)}
                        />
                        <span className="ad-h">Used in headers and notification emails</span>
                      </div>
                    </div>

                    <div className="ad-g3">
                      <div className="ad-fld">
                        <label>Organisation Slug (Login URL)</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.slug}
                          onChange={(e) => handleOrgFieldChange('slug', e.target.value)}
                        />
                      </div>
                      <div className="ad-fld">
                        <label>Industry</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.industry}
                          onChange={(e) => handleOrgFieldChange('industry', e.target.value)}
                        />
                      </div>
                      <div className="ad-fld">
                        <label>Company size</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.companySize}
                          onChange={(e) => handleOrgFieldChange('companySize', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="ad-g3">
                      <div className="ad-fld">
                        <label>Website</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.website}
                          onChange={(e) => handleOrgFieldChange('website', e.target.value)}
                        />
                      </div>
                      <div className="ad-fld">
                        <label>Headquarters</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.headquarters}
                          onChange={(e) => handleOrgFieldChange('headquarters', e.target.value)}
                        />
                      </div>
                      <div className="ad-fld">
                        <label>Founded</label>
                        <input
                          type="text"
                          className="ad-inp"
                          value={orgProfile.founded}
                          onChange={(e) => handleOrgFieldChange('founded', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: AI Context & Grounding */}
                <div className="ad-sc">
                  <div className="ad-sch">
                    <span className="ad-si">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />
                      </svg>
                    </span>
                    <div>
                      <h3>About the company for AI Personas</h3>
                      <p>Enables AI customer personas and rubrics to speak with realistic company context</p>
                    </div>
                  </div>

                  <div className="ad-scb">
                    <div className="ad-fld">
                      <label>Company overview & value proposition</label>
                      <textarea
                        className="ad-inp"
                        style={{ height: '80px', resize: 'vertical' }}
                        value={orgProfile.overview}
                        onChange={(e) => handleOrgFieldChange('overview', e.target.value)}
                      />
                    </div>

                    <div className="ad-g2">
                      <div className="ad-fld">
                        <label>Key Products & Services</label>
                        <div className="ad-chips">
                          {orgProfile.products.map((p, idx) => (
                            <span key={idx} className="ad-c">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="ad-fld">
                        <label>Customer Types</label>
                        <div className="ad-chips">
                          {orgProfile.customerTypes.map((c, idx) => (
                            <span key={idx} className="ad-c">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="ad-fld">
                      <label>Terminology Glossary (Say vs Instead of)</label>
                      <div className="ad-gl">
                        <div className="ad-h ad-y">Say</div>
                        <div className="ad-h">Instead of</div>
                        {orgProfile.glossary.map((item, idx) => (
                          <React.Fragment key={idx}>
                            <div className="ad-y"><i>✓</i>{item.say}</div>
                            <div className="ad-n"><i>✕</i><s>{item.insteadOf}</s></div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: Key Policies */}
                <div className="ad-sc">
                  <div className="ad-sch">
                    <span className="ad-si ad-v">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                    </span>
                    <div>
                      <h3>Key Policies & Quality Standards</h3>
                      <p>Operational standards evaluated during simulations</p>
                    </div>
                  </div>

                  <div className="ad-scb">
                    <div className="ad-pol">
                      {orgProfile.policies.map((pol, idx) => (
                        <div key={idx} className="ad-pr">
                          <div>
                            <b>{pol.title}</b>
                            <span>{pol.desc}</span>
                          </div>
                          {pol.file && (
                            <div className="ad-r">
                              <span className="ad-att">{pol.file}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: AI Readiness & Regional Formats */}
              <div className="ad-scol">
                {/* AI Readiness Meter */}
                <div className="ad-sc">
                  <div className="ad-sch">
                    <span className="ad-si ad-g">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 3 2.8 5.8 6.2.9-4.5 4.4 1 6.3L12 17.5 6.5 20.4l1-6.3L3 9.7l6.2-.9z" />
                      </svg>
                    </span>
                    <div>
                      <h3>Company Context for AI</h3>
                      <p>Grounding coverage</p>
                    </div>
                  </div>

                  <div className="ad-scb">
                    <div className="ad-meter">
                      <span className="ad-ring2">
                        <span>90%</span>
                      </span>
                      <div>
                        <b>High Context Precision</b>
                        <p>AI personas are accurately customized with your products, customer types, and policies.</p>
                      </div>
                    </div>

                    <div className="ad-cl">
                      <div className="ad-ok"><i>✓</i> Company overview</div>
                      <div className="ad-ok"><i>✓</i> Products & services</div>
                      <div className="ad-ok"><i>✓</i> Customer types</div>
                      <div className="ad-ok"><i>✓</i> Brand voice & words we use</div>
                      <div className="ad-ok"><i>✓</i> 4 key policies</div>
                    </div>
                  </div>
                </div>

                {/* Regional Formats */}
                <div className="ad-sc">
                  <div className="ad-sch">
                    <span className="ad-si ad-g">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                      </svg>
                    </span>
                    <div>
                      <h3>Regional Formats</h3>
                      <p>Due dates, reports, and currency formatting</p>
                    </div>
                  </div>

                  <div className="ad-scb">
                    <div className="ad-fld">
                      <label>Time zone</label>
                      <input
                        type="text"
                        className="ad-inp"
                        value={orgProfile.timezone}
                        onChange={(e) => handleOrgFieldChange('timezone', e.target.value)}
                      />
                    </div>
                    <div className="ad-fld">
                      <label>Currency</label>
                      <input
                        type="text"
                        className="ad-inp"
                        value={orgProfile.currency}
                        onChange={(e) => handleOrgFieldChange('currency', e.target.value)}
                      />
                    </div>
                    <div className="ad-fld">
                      <label>Working days</label>
                      <input
                        type="text"
                        className="ad-inp"
                        value={orgProfile.workingDays}
                        onChange={(e) => handleOrgFieldChange('workingDays', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: CUSTOM FIELDS & LEARNER SCHEMA                                     */}
        {/* ========================================================================= */}
        {activeTab === 'custom_fields' && (
          <div className="ad-sgrid">
            {/* Left Column: Schema Table */}
            <div className="ad-scol">
              <div className="ad-sc">
                <div className="ad-sch" style={{ justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="ad-si">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                    </span>
                    <div>
                      <h3>Configured Custom Fields</h3>
                      <p>{customFields.length} user attributes for profiling & cohort filtering</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ad-btn ad-pri"
                    style={{ fontSize: '12.5px', padding: '5px 12px' }}
                    onClick={() => setShowAddFieldModal(true)}
                  >
                    + Add Custom Field
                  </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table className="ad-N" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '12px 16px' }}>Field Label</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px' }}>Key</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px' }}>Type</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px' }}>Options</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Req.</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customFields.map((field) => (
                        <tr key={field.id}>
                          <td style={{ padding: '12px 16px' }}>
                            <b>{field.label}</b>
                            {field.usedInReports && (
                              <span className="ad-s" style={{ color: 'var(--blue)', fontWeight: 500 }}>
                                Used in Reports & Analytics filter
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <code style={{ background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                              {field.key}
                            </code>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span className="ad-pill2 ad-b" style={{ textTransform: 'capitalize' }}>
                              {field.type}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', maxWidth: '280px' }}>
                            {field.options && field.options.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {field.options.slice(0, 3).map((opt, i) => (
                                  <span key={i} style={{ fontSize: '11px', background: 'var(--line)', padding: '2px 6px', borderRadius: '4px' }}>
                                    {opt}
                                  </span>
                                ))}
                                {field.options.length > 3 && (
                                  <span style={{ fontSize: '11px', color: 'var(--muted)', alignSelf: 'center' }}>
                                    +{field.options.length - 3} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--faint)', fontSize: '12px' }}>Free-form text input</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                            <span
                              className={`ad-tg ${field.required ? 'ad-on' : ''}`}
                              onClick={() => handleToggleFieldRequired(field.id)}
                              title="Toggle required"
                            />
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                            <span
                              className={`ad-tg ${field.isActive ? 'ad-on' : ''}`}
                              onClick={() => handleToggleFieldActive(field.id)}
                              title="Toggle active"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right Column: Schema Overview & UDF Guidance */}
            <div className="ad-scol">
              <div className="ad-sc">
                <div className="ad-sch">
                  <span className="ad-si ad-g">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </span>
                  <div>
                    <h3>Schema & Dimension Summary</h3>
                    <p>Attributes used across Trainova</p>
                  </div>
                </div>

                <div className="ad-scb">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ background: 'var(--surface, #F8FAFC)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line, #E2E8F0)' }}>
                      <span style={{ fontSize: '11.5px', color: 'var(--muted, #64748B)', fontWeight: 600, textTransform: 'uppercase' }}>Active Fields</span>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--navy, #0F172A)', marginTop: '2px' }}>
                        {customFields.filter(f => f.isActive).length}
                      </div>
                    </div>
                    <div style={{ background: 'var(--surface, #F8FAFC)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line, #E2E8F0)' }}>
                      <span style={{ fontSize: '11.5px', color: 'var(--muted, #64748B)', fontWeight: 600, textTransform: 'uppercase' }}>Mandatory</span>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--blue, #3B5BDB)', marginTop: '2px' }}>
                        {customFields.filter(f => f.required).length}
                      </div>
                    </div>
                  </div>

                  <div className="ad-cl">
                    <div className="ad-ok"><i>✓</i> CSV bulk user onboarding validation</div>
                    <div className="ad-ok"><i>✓</i> Simulation cohort breakdown filters</div>
                    <div className="ad-ok"><i>✓</i> Analytical competency dimension tags</div>
                    <div className="ad-ok"><i>✓</i> REST API user payload mapping</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: NOTIFICATIONS & EMAIL TEMPLATES                                    */}
        {/* ========================================================================= */}
        {activeTab === 'notifications' && (
          <div className="ad-sgrid">
            {/* Left Column: What we send, Timing, Sender */}
            <div className="ad-scol">
              {/* Card 1: What we send */}
              <div className="ad-sc">
                <div className="ad-sch">
                  <span className="ad-si">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0" />
                    </svg>
                  </span>
                  <div>
                    <h3>What we send</h3>
                    <p>Turn each notification on or off for each channel</p>
                  </div>
                </div>

                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="ad-N" style={{ minWidth: '520px' }}>
                    <thead>
                      <tr>
                        <th>Notification</th>
                        <th className="ad-sp">Email</th>
                        <th>In-app</th>
                        <th>Template</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Learners Group */}
                      <tr className="ad-g">
                        <td colSpan={4}>To learners</td>
                      </tr>
                      {['simulation_assigned', 'due_date_reminder', 'overdue', 'results_ready'].map((key) => {
                        const ch = settings.channels[key] || DEFAULT_SETTINGS.channels[key];
                        const isSelected = selectedTemplateKey === key;
                        return (
                          <tr
                            key={key}
                            className={isSelected ? 'ad-cur' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedTemplateKey(key)}
                          >
                            <td>
                              <b>{ch.label}</b>
                              <span className="ad-s">{ch.sub}</span>
                            </td>
                            <td className="ad-sp" onClick={(e) => e.stopPropagation()}>
                              <span
                                className={`ad-tg ${ch.email ? 'ad-on' : ''}`}
                                onClick={() => handleToggleChannel(key, 'email')}
                                title="Toggle email notification"
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span
                                className={`ad-tg ${ch.inApp ? 'ad-on' : ''}`}
                                onClick={() => handleToggleChannel(key, 'inApp')}
                                title="Toggle in-app notification"
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span
                                className="ad-kb"
                                title="Edit email template"
                                onClick={() => handleOpenEditTemplate(key)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 20h4L19 9l-4-4L4 16z" />
                                  <path d="m13.5 6.5 4 4" />
                                </svg>
                              </span>
                            </td>
                          </tr>
                        );
                      })}

                      {/* Admins Group */}
                      <tr className="ad-g">
                        <td colSpan={4}>To admins</td>
                      </tr>
                      {['session_flagged', 'account_locked', 'weekly_summary', 'plan_limit_80'].map((key) => {
                        const ch = settings.channels[key] || DEFAULT_SETTINGS.channels[key];
                        const isSelected = selectedTemplateKey === key;
                        return (
                          <tr
                            key={key}
                            className={isSelected ? 'ad-cur' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedTemplateKey(key)}
                          >
                            <td>
                              <b>{ch.label}</b>
                              <span className="ad-s">{ch.sub}</span>
                            </td>
                            <td className="ad-sp" onClick={(e) => e.stopPropagation()}>
                              <span
                                className={`ad-tg ${ch.email ? 'ad-on' : ''}`}
                                onClick={() => handleToggleChannel(key, 'email')}
                                title="Toggle email notification"
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span
                                className={`ad-tg ${ch.inApp ? 'ad-on' : ''}`}
                                onClick={() => handleToggleChannel(key, 'inApp')}
                                title="Toggle in-app notification"
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span
                                className="ad-kb"
                                title="Edit email template"
                                onClick={() => handleOpenEditTemplate(key)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 20h4L19 9l-4-4L4 16z" />
                                  <path d="m13.5 6.5 4 4" />
                                </svg>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Card 2: Reminder Timing */}
              <div className="ad-sc">
                <div className="ad-sch">
                  <span className="ad-si ad-v">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                  </span>
                  <div>
                    <h3>Reminder timing</h3>
                    <p>For due-date reminders and overdue nudges</p>
                  </div>
                </div>

                <div className="ad-scb">
                  {/* Send reminders before due date */}
                  <div className="ad-set">
                    <div className="ad-t">
                      <b>Send reminders before the due date</b>
                      <span>Up to 3 reminders</span>
                    </div>
                    <div className="ad-c">
                      {settings.timing.reminderDays.map((day) => (
                        <span key={day} className="ad-pill2 ad-b" style={{ position: 'relative' }}>
                          {day} {day === 1 ? 'day' : 'days'}
                          <span
                            onClick={() => handleRemoveReminderDay(day)}
                            style={{ marginLeft: '4px', cursor: 'pointer', opacity: 0.6 }}
                            title="Remove reminder"
                          >
                            ×
                          </span>
                        </span>
                      ))}
                      {settings.timing.reminderDays.length < 3 && (
                        <span className="ad-sbtn" style={{ height: '28px' }} onClick={handleAddReminderDay}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Add
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Overdue Stepper */}
                  <div className="ad-set">
                    <div className="ad-t">
                      <b>Keep nudging overdue learners</b>
                      <span>Every week until the simulation is done or closed</span>
                    </div>
                    <div className="ad-c">
                      <span className="ad-stp">
                        <i onClick={() => handleNudgeWeeksChange(-1)} title="Decrease weeks">
                          −
                        </i>
                        <b>{settings.timing.maxOverdueNudgeWeeks} weeks max</b>
                        <i onClick={() => handleNudgeWeeksChange(1)} title="Increase weeks">
                          +
                        </i>
                      </span>
                    </div>
                  </div>

                  {/* Quiet hours */}
                  <div className="ad-set">
                    <div className="ad-t">
                      <b>Quiet hours</b>
                      <span>No emails between these times ({settings.timing.quietHoursTz || 'India Standard Time'})</span>
                    </div>
                    <div className="ad-c">
                      <input
                        type="text"
                        className="ad-inp ad-sm"
                        style={{ width: '96px', textAlign: 'center' }}
                        value={settings.timing.quietHoursStart}
                        onChange={(e) => handleQuietHoursChange('quietHoursStart', e.target.value)}
                      />
                      <span style={{ color: 'var(--faint)' }}>to</span>
                      <input
                        type="text"
                        className="ad-inp ad-sm"
                        style={{ width: '96px', textAlign: 'center' }}
                        value={settings.timing.quietHoursEnd}
                        onChange={(e) => handleQuietHoursChange('quietHoursEnd', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Manager Copy */}
                  <div className="ad-set">
                    <div className="ad-t">
                      <b>Copy the learner’s manager on overdue emails</b>
                      <span>Uses the Manager field on the learner record</span>
                    </div>
                    <div className="ad-c">
                      <span
                        className={`ad-tg ${settings.timing.copyManagerOnOverdue ? 'ad-on' : ''}`}
                        onClick={handleToggleManagerCopy}
                        title="Toggle manager copy"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Sender Details */}
              <div className="ad-sc">
                <div className="ad-sch">
                  <span className="ad-si ad-g">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                  </span>
                  <div>
                    <h3>Sender details</h3>
                    <p>How emails appear in the inbox</p>
                  </div>
                </div>

                <div className="ad-scb">
                  <div className="ad-g2">
                    <div className="ad-fld">
                      <label>From name</label>
                      <input
                        type="text"
                        className="ad-inp"
                        value={settings.sender.fromName}
                        onChange={(e) => handleSenderChange('fromName', e.target.value)}
                      />
                      <span className="ad-h">Sent from {settings.sender.fromEmail || 'no-reply@mail.trainova.ai'}</span>
                    </div>
                    <div className="ad-fld">
                      <label>Reply-to email</label>
                      <input
                        type="email"
                        className="ad-inp"
                        value={settings.sender.replyToEmail}
                        onChange={(e) => handleSenderChange('replyToEmail', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Custom SMTP Relay Configuration */}
              <div className="ad-sc">
                <div className="ad-sch" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="ad-si" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                        <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                        <line x1="6" y1="6" x2="6.01" y2="6" />
                        <line x1="6" y1="18" x2="6.01" y2="18" />
                      </svg>
                    </span>
                    <div>
                      <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Custom SMTP Relay
                        {settings.smtp?.enabled && (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: 'var(--ok-soft)', color: 'var(--ok)', fontWeight: 600 }}>
                            Active
                          </span>
                        )}
                      </h3>
                      <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--muted)' }}>
                        Route transactional learner and admin emails through your organization's mail server
                      </p>
                    </div>
                  </div>
                  <span
                    className={`ad-tg ${settings.smtp?.enabled ? 'ad-on' : ''}`}
                    onClick={() => handleSmtpChange('enabled', !settings.smtp?.enabled)}
                    title={settings.smtp?.enabled ? 'Disable custom SMTP' : 'Enable custom SMTP'}
                  />
                </div>

                <div className="ad-scb">
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: settings.smtp?.enabled ? 'var(--blue-soft)' : 'var(--surface)',
                      border: '1px solid var(--line)',
                      fontSize: '13px',
                      color: 'var(--ink)',
                      lineHeight: '1.5',
                    }}
                  >
                    {settings.smtp?.enabled ? (
                      <span>
                        <strong>Custom SMTP Active:</strong> Outbound simulation invites, reminders, evaluation reports, and test emails will use the credentials specified below.
                      </span>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>
                        <strong>Default Cloud Delivery Active:</strong> Custom SMTP is currently disabled. All emails are dispatched via Trainova's managed cloud mail delivery. Toggle the switch above to activate your custom relay.
                      </span>
                    )}
                  </div>

                  <div className="ad-g2" style={{ marginBottom: '16px' }}>
                    <div className="ad-fld">
                      <label>SMTP Host / Server <span style={{ color: 'var(--bad)' }}>*</span></label>
                      <input
                        type="text"
                        className="ad-inp"
                        placeholder="e.g. smtp.office365.com or smtp.gmail.com"
                        value={settings.smtp?.host || ''}
                        onChange={(e) => handleSmtpChange('host', e.target.value)}
                      />
                      <span className="ad-h">Mail server address or hostname</span>
                    </div>

                    <div className="ad-fld">
                      <label>SMTP Port <span style={{ color: 'var(--bad)' }}>*</span></label>
                      <input
                        type="number"
                        className="ad-inp"
                        placeholder="587"
                        value={settings.smtp?.port || 587}
                        onChange={(e) => {
                          const port = parseInt(e.target.value, 10) || 587;
                          handleSmtpChange('port', port);
                          if (port === 465) {
                            handleSmtpChange('secure', true);
                          } else if (port === 587 || port === 25) {
                            handleSmtpChange('secure', false);
                          }
                        }}
                      />
                      <span className="ad-h">Common ports: 587 (STARTTLS), 465 (SSL/TLS), 25</span>
                    </div>
                  </div>

                  <div className="ad-g2" style={{ marginBottom: '16px' }}>
                    <div className="ad-fld">
                      <label>Encryption Protocol</label>
                      <select
                        className="ad-inp"
                        value={settings.smtp?.secure ? 'ssl' : 'starttls'}
                        onChange={(e) => handleSmtpChange('secure', e.target.value === 'ssl')}
                      >
                        <option value="starttls">STARTTLS (Opportunistic TLS — Port 587 or 25)</option>
                        <option value="ssl">SSL / TLS (Direct TLS — Port 465)</option>
                      </select>
                      <span className="ad-h">Select SSL/TLS for port 465, STARTTLS for port 587</span>
                    </div>

                    <div className="ad-fld">
                      <label>SMTP Username / Account</label>
                      <input
                        type="text"
                        className="ad-inp"
                        placeholder="e.g. notifications@company.com"
                        value={settings.smtp?.user || ''}
                        onChange={(e) => handleSmtpChange('user', e.target.value)}
                      />
                      <span className="ad-h">Username or API key for SMTP authentication</span>
                    </div>
                  </div>

                  <div className="ad-g2" style={{ marginBottom: '16px' }}>
                    <div className="ad-fld">
                      <label>SMTP Password / App Secret</label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="ad-inp"
                          style={{ paddingRight: '50px' }}
                          placeholder={settings.smtp?.hasPassword ? '•••••••• (configured)' : 'Enter SMTP password or API secret'}
                          value={settings.smtp?.pass || ''}
                          onChange={(e) => handleSmtpChange('pass', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            background: 'none',
                            border: 'none',
                            color: 'var(--muted)',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '4px',
                          }}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <span className="ad-h">
                        {settings.smtp?.hasPassword && !settings.smtp?.pass
                          ? 'Password is configured securely. Leave blank to keep existing password.'
                          : 'Application password or SMTP relay secret'}
                      </span>
                    </div>

                    <div className="ad-fld">
                      <label>From Email Address</label>
                      <input
                        type="email"
                        className="ad-inp"
                        placeholder="training@company.com"
                        value={settings.smtp?.from || ''}
                        onChange={(e) => handleSmtpChange('from', e.target.value)}
                      />
                      <span className="ad-h">Verified sender mailbox address</span>
                    </div>
                  </div>

                  <div className="ad-g2" style={{ marginBottom: '18px' }}>
                    <div className="ad-fld">
                      <label>From Display Name</label>
                      <input
                        type="text"
                        className="ad-inp"
                        placeholder="TrainerBay Academy"
                        value={settings.smtp?.fromName || ''}
                        onChange={(e) => handleSmtpChange('fromName', e.target.value)}
                      />
                      <span className="ad-h">Friendly sender name shown in inbox</span>
                    </div>

                    <div className="ad-fld">
                      <label>Reply-To Email Address</label>
                      <input
                        type="email"
                        className="ad-inp"
                        placeholder="support@company.com"
                        value={settings.smtp?.replyTo || ''}
                        onChange={(e) => handleSmtpChange('replyTo', e.target.value)}
                      />
                      <span className="ad-h">Where learner replies are routed</span>
                    </div>
                  </div>

                  {/* Connection Verification Status Feedback */}
                  {connectionStatus && (
                    <div
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '13px',
                        background: connectionStatus.type === 'success' ? 'var(--ok-soft)' : 'var(--bad-soft)',
                        color: connectionStatus.type === 'success' ? 'var(--ok)' : 'var(--bad)',
                        border: `1px solid ${connectionStatus.type === 'success' ? 'rgba(15, 138, 95, 0.2)' : 'rgba(194, 59, 59, 0.2)'}`,
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>{connectionStatus.type === 'success' ? '✓' : '⚠️'}</span>
                      <span>{connectionStatus.message}</span>
                    </div>
                  )}

                  {/* Action Buttons Toolbar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', borderTop: '1px solid var(--line)', paddingTop: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="ad-btn"
                        onClick={handleTestSmtpConnection}
                        disabled={isTestingConnection || !settings.smtp?.host}
                        style={{ background: 'var(--white)', borderColor: 'var(--line)' }}
                      >
                        {isTestingConnection ? 'Testing Handshake…' : '🔌 Test Connection'}
                      </button>

                      <button
                        type="button"
                        className="ad-btn"
                        onClick={() => {
                          setSmtpTestRecipient(testEmail || user?.email || '');
                          setShowSmtpTestModal(true);
                        }}
                        disabled={!settings.smtp?.host}
                        style={{ background: 'var(--blue-soft)', color: 'var(--blue)', borderColor: 'transparent' }}
                      >
                        📨 Send Test Email
                      </button>
                    </div>

                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      Remember to click "Save changes" below to persist credentials
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Email Preview */}
            <div className="ad-scol">
              <div className="ad-sc">
                <div className="ad-sch">
                  <span className="ad-si">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                  </span>
                  <div>
                    <h3>Preview · {activeTemplate.name}</h3>
                    <p>Email · English (India)</p>
                  </div>
                  <div className="ad-r">
                    <span className="ad-sbtn" onClick={() => setShowTestModal(true)}>
                      Send test
                    </span>
                  </div>
                </div>

                <div className="ad-scb">
                  {/* Email Card Container */}
                  <div className="ad-mail">
                    <div className="ad-mh">
                      <span>
                        From: <b>{settings.sender.fromName || 'TrainerBay Academy'}</b>
                      </span>
                      <span>
                        Subject: <b>{activeTemplate.subject}</b>
                      </span>
                    </div>

                    <div className="ad-mb">
                      <div className="ad-brandm">
                        <span className="ad-tm">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                            <path d="M3 14q3-6 6 0t6 0 6 0" />
                          </svg>
                        </span>
                        {settings.sender.fromName || 'TrainerBay Academy'}
                      </div>

                      <h4>
                        {activeTemplate.heading.includes('{{first_name}}') ? (
                          <>
                            {activeTemplate.heading.split('{{first_name}}')[0]}
                            <span className="ad-var">first_name</span>
                            {activeTemplate.heading.split('{{first_name}}')[1]}
                          </>
                        ) : (
                          activeTemplate.heading
                        )}
                      </h4>

                      <p style={{ margin: 0 }}>{activeTemplate.description}</p>

                      <div className="ad-box">
                        <b>{activeTemplate.boxTitle}</b>
                        <span>
                          {activeTemplate.boxMeta.includes('{{due_date}}') ? (
                            <>
                              {activeTemplate.boxMeta.split('{{due_date}}')[0]}
                              <span className="ad-var">due_date</span>
                              {activeTemplate.boxMeta.split('{{due_date}}')[1]}
                            </>
                          ) : (
                            activeTemplate.boxMeta
                          )}
                        </span>
                      </div>

                      <span className="ad-cta">{activeTemplate.ctaText || 'Open simulation'}</span>

                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--faint)' }}>
                        Questions? Reply to this email or write to {settings.sender.replyToEmail}
                      </p>
                    </div>

                    <div className="ad-mf">
                      Powered by Trainova AI · You get this because {orgProfile.displayName || settings.tenantName || 'TrainerBay'} assigned you training
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span className="ad-sbtn" onClick={() => handleOpenEditTemplate(selectedTemplateKey)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 20h4L19 9l-4-4L4 16z" />
                        <path d="m13.5 6.5 4 4" />
                      </svg>
                      Edit template
                    </span>
                    <span className="ad-sbtn" onClick={() => handleResetTemplateToDefault(selectedTemplateKey)}>
                      Reset to default
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Languages & Localization */}
        {activeTab === 'languages' && <TenantLanguageSettingsTab />}

        {/* Floating Save Bar when dirty */}
        {isDirty && (
          <div className="ad-savebar">
            <span className="ad-dot" />
            <span>
              You have unsaved changes in <b>{activeTab === 'org' ? 'Organisation Profile' : activeTab === 'custom_fields' ? 'Custom Fields' : 'Notification Settings'}</b>
            </span>
            <div className="ad-r">
              <span className="ad-sbtn" onClick={handleDiscard}>
                Discard
              </span>
              <span className="ad-btn ad-pri" onClick={handleSaveChanges}>
                {isSaving ? 'Saving…' : 'Save changes'}
              </span>
            </div>
          </div>
        )}

        {/* Add Custom Field Modal */}
        {showAddFieldModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(7, 29, 69, 0.45)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 100,
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowAddFieldModal(false)}
          >
            <div
              className="ad-sc ad-modal-card"
              style={{ width: '100%', maxWidth: '480px', margin: '0 14px', boxSizing: 'border-box', padding: 'clamp(16px, 4vw, 24px)', boxShadow: '0 20px 50px rgba(7, 29, 69, 0.3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Add Custom Learner Field</h3>
                <span
                  style={{ cursor: 'pointer', fontSize: '18px', color: 'var(--muted)' }}
                  onClick={() => setShowAddFieldModal(false)}
                >
                  ✕
                </span>
              </div>

              <div style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
                <div className="ad-fld">
                  <label>Field Name / Label</label>
                  <input
                    type="text"
                    className="ad-inp"
                    placeholder="e.g. Dealership Zone, Cost Center"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                  />
                </div>

                <div className="ad-fld">
                  <label>Data Type</label>
                  <select
                    className="ad-inp"
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value as any)}
                  >
                    <option value="select">Dropdown Single-Select</option>
                    <option value="text">Free Text Input</option>
                    <option value="number">Number</option>
                  </select>
                </div>

                {newFieldType === 'select' && (
                  <div className="ad-fld">
                    <label>Select Options (Comma-separated)</label>
                    <input
                      type="text"
                      className="ad-inp"
                      placeholder="e.g. Zone A, Zone B, Zone C"
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="req_fld"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                  />
                  <label htmlFor="req_fld" style={{ fontSize: '13px', cursor: 'pointer' }}>
                    Mark this field as mandatory for new learners
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <span className="ad-sbtn" onClick={() => setShowAddFieldModal(false)}>
                  Cancel
                </span>
                <span className="ad-btn ad-pri" onClick={handleAddCustomField}>
                  Add Field
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Send Test Email Modal */}
        {showTestModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(7, 29, 69, 0.45)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 100,
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowTestModal(false)}
          >
            <div
              className="ad-sc ad-modal-card"
              style={{ width: '100%', maxWidth: '460px', margin: '0 14px', boxSizing: 'border-box', padding: 'clamp(16px, 4vw, 24px)', boxShadow: '0 20px 50px rgba(7, 29, 69, 0.3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Send test email</h3>
                <span
                  style={{ cursor: 'pointer', fontSize: '18px', color: 'var(--muted)' }}
                  onClick={() => setShowTestModal(false)}
                >
                  ✕
                </span>
              </div>

              <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--muted)' }}>
                Send a real sample of <b>{activeTemplate.name}</b> template to verify inbox formatting and branding.
              </p>

              <div className="ad-fld" style={{ marginBottom: '20px' }}>
                <label>Recipient email address</label>
                <input
                  type="email"
                  className="ad-inp"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your.email@company.com"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <span className="ad-sbtn" onClick={() => setShowTestModal(false)}>
                  Cancel
                </span>
                <span className="ad-btn ad-pri" onClick={handleSendTestEmail}>
                  {isSendingTest ? 'Sending…' : 'Send test email'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Send SMTP Test Email Modal */}
        {showSmtpTestModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(7, 29, 69, 0.45)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 100,
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowSmtpTestModal(false)}
          >
            <div
              className="ad-sc ad-modal-card"
              style={{ width: '100%', maxWidth: '480px', margin: '0 14px', boxSizing: 'border-box', padding: 'clamp(16px, 4vw, 24px)', boxShadow: '0 20px 50px rgba(7, 29, 69, 0.3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Send SMTP Relay Test Email</h3>
                <span
                  style={{ cursor: 'pointer', fontSize: '18px', color: 'var(--muted)' }}
                  onClick={() => setShowSmtpTestModal(false)}
                >
                  ✕
                </span>
              </div>

              <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--muted)' }}>
                Dispatches a diagnostic message directly through <b>{settings.smtp?.host || 'your SMTP server'}</b> to verify TLS encryption, credentials, and recipient deliverability.
              </p>

              <div className="ad-fld" style={{ marginBottom: '20px' }}>
                <label>Recipient Email Address <span style={{ color: 'var(--bad)' }}>*</span></label>
                <input
                  type="email"
                  className="ad-inp"
                  value={smtpTestRecipient}
                  onChange={(e) => setSmtpTestRecipient(e.target.value)}
                  placeholder="your.email@company.com"
                />
                <span className="ad-h">A real email inbox you have access to</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="ad-btn" onClick={() => setShowSmtpTestModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ad-btn ad-pri"
                  onClick={handleSendSmtpTestEmail}
                  disabled={isSendingSmtpTest || !smtpTestRecipient}
                >
                  {isSendingSmtpTest ? 'Sending via SMTP…' : 'Send Test Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Template Modal */}
        {showEditTemplateModal && editingTemplate && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(7, 29, 69, 0.45)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 100,
              backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowEditTemplateModal(false)}
          >
            <div
              className="ad-sc ad-modal-card"
              style={{ width: '100%', maxWidth: '600px', margin: '0 14px', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', padding: 'clamp(16px, 4vw, 24px)', boxShadow: '0 20px 50px rgba(7, 29, 69, 0.3)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Edit template · {editingTemplate.name}</h3>
                <span
                  style={{ cursor: 'pointer', fontSize: '18px', color: 'var(--muted)' }}
                  onClick={() => setShowEditTemplateModal(false)}
                >
                  ✕
                </span>
              </div>

              <div style={{ display: 'grid', gap: '14px', marginBottom: '24px' }}>
                <div className="ad-fld">
                  <label>Email Subject</label>
                  <input
                    type="text"
                    className="ad-inp"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  />
                </div>

                <div className="ad-fld">
                  <label>Greeting Heading</label>
                  <input
                    type="text"
                    className="ad-inp"
                    value={editingTemplate.heading}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, heading: e.target.value })}
                  />
                  <span className="ad-h">Variables allowed: <code>&#123;&#123;first_name&#125;&#125;</code>, <code>&#123;&#123;learner_name&#125;&#125;</code></span>
                </div>

                <div className="ad-fld">
                  <label>Main Body Description</label>
                  <textarea
                    className="ad-inp"
                    style={{ height: '70px', padding: '8px 12px', resize: 'vertical' }}
                    value={editingTemplate.description}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  />
                </div>

                <div className="ad-fld">
                  <label>Callout Box Title</label>
                  <input
                    type="text"
                    className="ad-inp"
                    value={editingTemplate.boxTitle}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, boxTitle: e.target.value })}
                  />
                </div>

                <div className="ad-fld">
                  <label>Callout Box Details</label>
                  <input
                    type="text"
                    className="ad-inp"
                    value={editingTemplate.boxMeta}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, boxMeta: e.target.value })}
                  />
                  <span className="ad-h">Variables allowed: <code>&#123;&#123;due_date&#125;&#125;</code>, <code>&#123;&#123;score&#125;&#125;</code></span>
                </div>

                <div className="ad-fld">
                  <label>Action Button (CTA) Label</label>
                  <input
                    type="text"
                    className="ad-inp"
                    value={editingTemplate.ctaText}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, ctaText: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <span className="ad-sbtn" onClick={() => setShowEditTemplateModal(false)}>
                  Cancel
                </span>
                <span className="ad-btn ad-pri" onClick={handleSaveTemplateModal}>
                  Apply changes
                </span>
              </div>
            </div>
          </div>
        )}
      </>
    )}
  </div>
);
};

export default TenantNotificationSettingsPage;

